import { FC, useEffect, useMemo, useState } from "react";
import type { FileRejection } from "react-dropzone";
import { FormikProps } from "formik";
import countries from "world-countries";

import { DateTime } from "luxon";
import {
  DocumentIcon,
  TickCircleIcon,
  TrashIcon,
  UploadIcon,
  XlsFileTypeIcon,
} from "../../../assets/icons";
import {
  Button,
  CardList,
  Combobox,
  Dropzone,
  Modal,
} from "../../../components";
import { formatBytes } from "../../../utils";
import {
  CardVariantType,
  DisplayVariant,
} from "../../../components/CardList/CardList";
import { DrawdownFields } from "../../LoanCalculator/service";
import { BulkDrawdownSummaryFields } from "./BulkDrawdownSummaryFields";
import { DrawdownFormBulkParsePanels } from "./DrawdownFormBulkParsePanels";
import { InvoicesList } from "./InvoicesList";
import {
  useCalculateCurrencyConversion,
  useGetApplicationVendors,
  useGetEligibleDrawdownCurrencies,
  useDownloadBulkDrawdownTemplate,
} from "../../../hooks";
import {
  LoanProductType,
  LoanVendorStatus,
  LoanVendorType,
  MediaPurpose,
} from "../../../hooks/api/types";
import { useLoanDashboard } from "../../../contexts";
import { getFileIcon } from "../../../utils/fileIcon.util";
import { getDropzoneAcceptForPurpose } from "../../../utils/accept.util";
import { BulkParseState, InvoiceEntryModeType } from "./service";
import {
  DEFAULT_CURRENCY,
  BulkParseStatus,
  InvoiceEntryMode,
} from "./constants";

type DrawdownFormProps = {
  formik: FormikProps<DrawdownFields>;
  availableLimit: number;
  maxDiscountLimit: number;
  loanApplicationId: string;
  previousStep?: () => void;
  loading?: boolean;
  error?: string;
  bulkParseState: BulkParseState;
  bulkParsing?: boolean;
  invoiceEntryMode: InvoiceEntryModeType;
  onInvoiceEntryModeChange: (mode: InvoiceEntryModeType) => void;
  onBulkParseTemplate: (templateFile: File) => Promise<void>;
  onBulkClearTemplate: () => void;
};

export const DrawdownForm: FC<DrawdownFormProps> = ({
  formik,
  availableLimit,
  maxDiscountLimit,
  loanApplicationId,
  previousStep,
  loading,
  error,
  bulkParseState,
  bulkParsing,
  invoiceEntryMode,
  onInvoiceEntryModeChange,
  onBulkParseTemplate,
  onBulkClearTemplate,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>("");
  const [bulkTemplateError, setBulkTemplateError] = useState<string>("");
  const [vendorActionError, setVendorActionError] = useState<string>("");
  const [bulkInvoicesExpanded, setBulkInvoicesExpanded] = useState(true);
  const [bulkTemplateModalOpen, setBulkTemplateModalOpen] = useState(false);
  const [bulkTemplateFile, setBulkTemplateFile] = useState<File | null>(null);
  const [bulkTemplateDraftFile, setBulkTemplateDraftFile] =
    useState<File | null>(null);
  const { mutateAsync: downloadBulkTemplate } =
    useDownloadBulkDrawdownTemplate();
  const {
    state: { currentLoanApplication },
  } = useLoanDashboard();
  const { data: supportedCurrencies } = useGetEligibleDrawdownCurrencies();
  const { mutateAsync: convertAmount } = useCalculateCurrencyConversion();

  const productType =
    currentLoanApplication?.loanProduct?.type ||
    currentLoanApplication?.loanType;
  const isPayablesFacility =
    productType === LoanProductType.LOC_PAYABLE_FINANCING;

  const { data: vendors } = useGetApplicationVendors({
    loanApplicationId,
    disablePagination: true,
    status: LoanVendorStatus.ACTIVE,
    type: isPayablesFacility ? LoanVendorType.SUPPLIER : LoanVendorType.BUYER,
  });

  const vendorOptions =
    vendors?.data?.map((vendor) => {
      const countryFlag = countries.find(
        (country) => country.cca3 === vendor.country
      )?.flag;
      return {
        value: vendor.vendorId,
        label: vendor.name,
        flag: countryFlag,
      };
    }) ?? [];

  const vendorLabel = isPayablesFacility ? "Supplier name" : "Buyer name";

  const handleFileClose = (index: number) => {
    const nextFiles = values.invoiceFiles.filter((_, i) => i !== index);
    setFieldValue("invoiceFiles", nextFiles);
    if (nextFiles.length <= 10) {
      setUploadError("");
    }
  };

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    setFieldTouched,
    setFieldValue,
    isValid,
    dirty,
  } = formik;

  const selectedVendorOption = useMemo(() => {
    if (!values.vendorId) {
      return undefined;
    }
    return vendorOptions.find((option) => option.value === values.vendorId);
  }, [values.vendorId, vendorOptions]);

  const handleUpload = (files: File[]) => {
    if (!files?.length) return;
    setUploading(true);
    const existingFiles = values.invoiceFiles || [];
    const availableSlots = Math.max(0, 10 - existingFiles.length);
    const MAX_SIZE = 5 * 1024 * 1024;

    const oversized = files.filter((f) => f.size > MAX_SIZE);
    const sizeOk = files.filter((f) => f.size <= MAX_SIZE);

    const filesToAdd = sizeOk.slice(0, availableSlots);

    if (!filesToAdd.length) {
      if (oversized.length) {
        setUploadError("Each file must be 5 MB or less.");
      } else {
        setUploadError("You can upload up to 10 files.");
      }
      setUploading(false);
      return;
    }

    if (sizeOk.length > availableSlots) {
      setUploadError("You can upload up to 10 files.");
    } else if (oversized.length) {
      setUploadError("Each file must be 5 MB or less.");
    } else {
      setUploadError("");
    }

    setFieldValue("invoiceFiles", [...existingFiles, ...filesToAdd]);
    setUploading(false);
  };

  const handleDropRejected = (rejections: FileRejection[]) => {
    if (!rejections?.length) return;
    const messages = new Set<string>();
    rejections.forEach((rej) => {
      rej.errors.forEach((err) => {
        switch (err.code) {
          case "file-too-large":
            messages.add("Each file must be 5 MB or less.");
            break;
          case "too-many-files":
            messages.add("You can upload up to 10 files.");
            break;
          case "file-invalid-type":
            messages.add("Only PDF files are allowed.");
            break;
          default:
            if (err.message) messages.add(err.message);
        }
      });
    });
    setUploadError(Array.from(messages).join(" "));
    setUploading(false);
  };

  useEffect(() => {
    if (invoiceEntryMode === InvoiceEntryMode.MANUAL) {
      setBulkTemplateModalOpen(false);
      setBulkTemplateFile(null);
      setBulkTemplateDraftFile(null);
      setBulkTemplateError("");
      onBulkClearTemplate();
    }
  }, [invoiceEntryMode, onBulkClearTemplate]);

  useEffect(() => {
    if (selectedVendorOption) {
      setVendorActionError("");
    }
  }, [selectedVendorOption]);

  const parseDateValueForUI = (value?: string) => {
    if (!value) return undefined;
    const iso = DateTime.fromISO(value);
    if (iso.isValid) return iso.toJSDate();
    const formatted = DateTime.fromFormat(value, "d LLLL, yyyy");
    if (formatted.isValid) return formatted.toJSDate();
    return undefined;
  };

  const isBulkIdleView =
    invoiceEntryMode === InvoiceEntryMode.BULK &&
    bulkParseState.status === BulkParseStatus.IDLE &&
    !bulkParsing &&
    !bulkTemplateFile;

  const isBulkProgressView =
    invoiceEntryMode === InvoiceEntryMode.BULK &&
    (bulkTemplateFile ||
      bulkParseState.status !== BulkParseStatus.IDLE ||
      bulkParsing);

  const summaryCurrency = formik.values.currency || "AED";
  const isBulkAmountOverAvailableLimit =
    invoiceEntryMode === InvoiceEntryMode.BULK &&
    bulkParseState.status === BulkParseStatus.SUCCESS &&
    typeof bulkParseState.totalAmount === "number" &&
    bulkParseState.totalAmount > availableLimit;

  const currencyOptions = useMemo(() => {
    if (
      supportedCurrencies?.currencies &&
      supportedCurrencies?.currencies?.length > 0
    ) {
      return supportedCurrencies.currencies.map((code: string) => ({
        value: code,
        label: code,
      }));
    } else {
      return [
        {
          value: DEFAULT_CURRENCY,
          label: DEFAULT_CURRENCY,
        },
      ];
    }
  }, [supportedCurrencies]);

  // Single debounced effect to convert totalAmount → AED whenever currency or totalAmount changes.
  // Using a single effect avoids the duplicate API call that occurred when currency changed
  // (the old immediate effect + the debounced effect both fired for the same currency change).
  useEffect(() => {
    const selectedCurrency = values.currency;

    // Nothing to convert when the selected currency is AED or absent
    if (
      selectedCurrency === DEFAULT_CURRENCY ||
      !selectedCurrency ||
      !values.totalAmount ||
      values.totalAmount <= 0
    ) {
      setFieldValue("requestedAmountInAED", undefined);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const aedConversion = await convertAmount({
          loanApplicationId: loanApplicationId,
          targetCurrency: DEFAULT_CURRENCY,
          sourceCurrency: selectedCurrency,
          amount: values.totalAmount,
        });
        const requestedAmountInAED = Number(aedConversion.convertedAmount);
        setFieldValue(
          "requestedAmountInAED",
          Number.isFinite(requestedAmountInAED)
            ? requestedAmountInAED
            : undefined
        );
      } catch {
        setFieldValue("requestedAmountInAED", undefined);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [values.currency, values.totalAmount, setFieldValue]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="cx-bg-white cx-rounded-2xl cx-p-6 cx-mb-6 cx-shadow-base">
        <div className="cx-flex cx-flex-col cx-gap-1 cx-mb-6">
          <h2 className="cx-text-lg cx-font-semibold">Basic details</h2>
        </div>
        <div className="cx-grid cx-grid-cols-1 md:cx-grid-cols-2 cx-gap-4">
          <div className="cx-mb-2">
            <Combobox
              placeholder={vendorLabel}
              options={vendorOptions}
              className="cx-rounded-lg"
              showSelectedIndicator
              noOptionsMessage="No active vendors found"
              inputProps={{
                name: "vendorId",
                value: selectedVendorOption,
                onBlur: () => setFieldTouched("vendorId"),
              }}
              onChange={(selectedValue) => {
                setFieldValue("vendorId", selectedValue || "");
                setFieldTouched("vendorId", true, false);
              }}
              error={touched.vendorId ? errors.vendorId : ""}
            />
          </div>
          <div className="cx-mb-2">
            <Combobox
              placeholder="Currency"
              options={currencyOptions}
              className="cx-rounded-lg"
              defaultValue={{
                label: DEFAULT_CURRENCY,
                value: DEFAULT_CURRENCY,
              }}
              showSelectedIndicator
              inputProps={{
                name: "currency",
                onBlur: () => setFieldTouched("currency"),
              }}
              onChange={handleChange("currency")}
              error={touched.currency ? errors.currency : ""}
            />
          </div>
        </div>
      </div>

      <div className="cx-bg-white cx-rounded-2xl cx-p-6 cx-mb-6 cx-shadow-base">
        <div className="cx-flex cx-flex-col cx-gap-1 cx-mb-4">
          <h2 className="cx-text-lg cx-font-semibold">
            How would you like to add invoices?
          </h2>
        </div>
        <div className="cx-grid cx-grid-cols-1 sm:cx-grid-cols-2 cx-gap-3">
          {[
            { label: "Enter manually", value: InvoiceEntryMode.MANUAL },
            { label: "Upload in bulk", value: InvoiceEntryMode.BULK },
          ].map((option) => {
            const selected = invoiceEntryMode === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={`cx-flex cx-items-center cx-gap-2 cx-rounded-lg cx-border cx-px-4 cx-py-3 cx-text-sm cx-font-medium cx-border-gray-200 cx-bg-white cx-text-text-secondary
                  `}
                onClick={() => onInvoiceEntryModeChange(option.value)}
              >
                <span
                  className={`cx-inline-flex cx-w-4 cx-h-4 cx-rounded-full cx-items-center cx-justify-center ${
                    selected ? " cx-bg-[#0064FF]" : ""
                  }`}
                >
                  {selected && (
                    <span className="cx-w-1.5 cx-h-1.5 cx-rounded-full cx-bg-white" />
                  )}
                </span>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>

        {isBulkIdleView && (
          <>
            <div className="cx-mt-4 cx-flex cx-flex-col cx-gap-4 cx-rounded-xl cx-border cx-border-stroke-primary cx-bg-[#FAFBFC] cx-p-3">
              <div className="cx-flex cx-flex-col  lg:cx-flex-row lg:cx-items-center lg:cx-justify-between">
                <div className="cx-flex cx-min-w-0 cx-items-start cx-gap-3">
                  <div
                    className="cx-relative cx-flex cx-h-[52px] cx-w-[52px] cx-flex-shrink-0 cx-items-center cx-justify-center"
                    aria-hidden
                  >
                    <div className="cx-absolute cx-h-[48px] cx-w-[48px] cx-rounded-full cx-border-2 cx-border-[#0064FF]/15" />
                    <div className="cx-absolute cx-h-[35px] cx-w-[35px] cx-rounded-full cx-border-2 cx-border-[#0064FF]/40" />
                    <DocumentIcon
                      width={20}
                      height={20}
                      className="cx-text-[#0064FF]"
                    />
                  </div>
                  <div>
                    <div className="cx-text-base cx-font-semibold cx-text-text-primary">
                      Bulk drawdown
                    </div>
                    <p className="cx-mt-1 cx-max-w-xl cx-text-sm cx-text-text-secondary">
                      Download the template, add your invoices, and upload in
                      one go.
                    </p>
                  </div>
                </div>
                <div className="cx-flex cx-shrink-0 cx-flex-wrap cx-items-center cx-gap-3">
                  <button
                    type="button"
                    className="cx-inline-flex cx-items-center cx-gap-2 cx-rounded-btn cx-border cx-border-stroke-primary cx-bg-interaction-button-primary-inverse cx-px-4 cx-py-2 cx-text-sm cx-font-medium cx-text-[#217346] hover:cx-bg-neutral-subtle"
                    onClick={async () => {
                      if (!selectedVendorOption) {
                        setVendorActionError(
                          "Please select a vendor first to continue."
                        );
                        return;
                      }
                      setVendorActionError("");
                      const vendorName = selectedVendorOption.label;
                      const currency = values.currency || "AED";
                      await downloadBulkTemplate({
                        loanApplicationId,
                        vendorName,
                        currency,
                      });
                    }}
                  >
                    <XlsFileTypeIcon width={20} height={20} aria-hidden />
                    Download template
                  </button>
                  <button
                    type="button"
                    className="cx-inline-flex cx-items-center cx-gap-2 cx-rounded-btn cx-border cx-border-stroke-primary cx-bg-interaction-button-primary-inverse cx-px-4 cx-py-2 cx-text-sm cx-font-medium cx-text-[#335CFF] hover:cx-bg-neutral-subtle"
                    onClick={() => {
                      if (!selectedVendorOption) {
                        setVendorActionError(
                          "Please select a vendor first to continue."
                        );
                        return;
                      }
                      setVendorActionError("");
                      setBulkTemplateError("");
                      setBulkTemplateDraftFile(null);
                      setBulkTemplateModalOpen(true);
                    }}
                  >
                    <UploadIcon width={20} height={20} aria-hidden />
                    Upload here
                  </button>
                  {vendorActionError && (
                    <span className="cx-text-xs cx-text-text-error">
                      {vendorActionError}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="cx-my-4 cx-border-t cx-border-gray-200" />
            <div>
              <BulkDrawdownSummaryFields
                currency={summaryCurrency}
                isIdle
                bulkParseState={bulkParseState}
              />
            </div>

            <div className="cx-mt-4 cx-flex cx-flex-col cx-items-center cx-rounded-xl cx-border cx-border-stroke-primary cx-bg-white cx-px-6 cx-py-10 cx-text-center">
              <div className="cx-inline-flex cx-items-center cx-justify-center cx-w-12 cx-h-12 cx-rounded-lg cx-border cx-border-gray-200 cx-mb-3">
                <DocumentIcon
                  width={24}
                  height={24}
                  className="cx-text-interaction-button-primary-default"
                />
              </div>
              <div className="cx-font-semibold cx-text-text-primary">
                No data yet
              </div>
              <div className="cx-text-sm cx-text-text-secondary cx-mt-1 cx-max-w-md">
                There is no data to show right now. Invoice data will be updated
                after processing the bulk upload file.
              </div>
            </div>
          </>
        )}

        {isBulkProgressView && (
          <>
            <div className="cx-mt-4 cx-rounded-xl cx-border cx-border-stroke-primary cx-bg-[#FAFBFC] cx-p-3">
              <div className="cx-flex cx-flex-col lg:cx-flex-row lg:cx-items-center lg:cx-justify-between cx-gap-4">
                <div className="cx-flex cx-items-center cx-gap-3 cx-min-w-0">
                  <div
                    className="cx-relative cx-flex cx-h-[52px] cx-w-[52px] cx-flex-shrink-0 cx-items-center cx-justify-center"
                    aria-hidden
                  >
                    <div className="cx-absolute cx-h-[48px] cx-w-[48px] cx-rounded-full cx-border-2 cx-border-[#079455]/15 " />
                    <div className="cx-absolute cx-h-[35px] cx-w-[35px] cx-rounded-full cx-border-2 cx-border-[#079455]/40" />
                    <TickCircleIcon width={20} height={20} />
                  </div>
                  <div>
                    <div className="cx-font-semibold cx-text-text-primary cx-text-base">
                      Bulk drawdown
                    </div>
                    <p className="cx-text-sm cx-text-text-secondary cx-mt-0.5">
                      File added successfully
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="cx-inline-flex cx-items-center cx-gap-2 cx-px-4 cx-py-2 cx-rounded-btn cx-border cx-border-[#A2191F] cx-bg-white cx-text-sm cx-font-medium cx-text-[#A2191F] hover:cx-bg-[#FFF5F5]"
                  onClick={() => {
                    setBulkTemplateFile(null);
                    setBulkTemplateError("");
                    onBulkClearTemplate();
                  }}
                >
                  <TrashIcon
                    width={16}
                    height={16}
                    className="cx-text-[#A2191F]"
                  />
                  Remove file
                </button>
              </div>
            </div>
            <div className="cx-my-4 cx-border-t cx-border-gray-200" />
            <BulkDrawdownSummaryFields
              currency={summaryCurrency}
              isIdle={false}
              bulkParseState={bulkParseState}
            />
          </>
        )}
      </div>

      {invoiceEntryMode === InvoiceEntryMode.MANUAL && (
        <InvoicesList
          availableLimit={availableLimit}
          maxDiscountLimit={maxDiscountLimit}
          formik={formik}
        />
      )}

      {invoiceEntryMode === InvoiceEntryMode.BULK && (
        <DrawdownFormBulkParsePanels
          currency={summaryCurrency}
          bulkParseState={bulkParseState}
          bulkInvoicesExpanded={bulkInvoicesExpanded}
          onToggleInvoicesExpanded={() =>
            setBulkInvoicesExpanded((prev) => !prev)
          }
          parseDateValueForUI={parseDateValueForUI}
        />
      )}
      <div className="cx-w-full">
        <div className="cx-bg-white cx-rounded-2xl cx-p-6 cx-mb-2 cx-shadow-base">
          <div className="cx-flex cx-flex-col cx-gap-1 cx-mb-4">
            <h2 className="cx-text-lg cx-font-semibold">
              Upload invoice & supporting documents
            </h2>
            <p className="cx-text-sm cx-text-gray-500">
              Please upload your invoices and credit notes individually or
              combine them into a single PDF.
            </p>
          </div>

          <Dropzone
            variant={uploading ? "uploading" : "invoice"}
            extensions={["Supports up to 10 files (max 5 MB each)"]}
            showExtensions={false}
            options={{
              accept: getDropzoneAcceptForPurpose(
                MediaPurpose.DRAWDOWN_INVOICE
              ),
              maxFiles: 10,
              maxSize: 5 * 1024 * 1024,
              onDropRejected: handleDropRejected,
            }}
            onDrop={(files) => {
              handleUpload(files);
            }}
            error={uploadError}
          />

          <div className="cx-w-full cx-space-y-2 cx-mt-3 ">
            {values.invoiceFiles.map((invoice: File, index: number) => (
              <CardList
                key={index}
                variant={
                  uploading
                    ? CardVariantType.UPLOADING
                    : invoice.name
                    ? CardVariantType.UPLOADED
                    : CardVariantType.UPLOAD
                }
                title={invoice.name}
                description={formatBytes(invoice.size, 2)}
                inputProps={{ name: "invoice" }}
                displayVariant={DisplayVariant.NEW}
                icon={
                  uploading ? (
                    <DocumentIcon width="100%" height="100%" />
                  ) : (
                    getFileIcon(invoice)
                  )
                }
                handleFileClose={() => handleFileClose(index)}
                onChange={async (file) => {
                  if (!file) return;
                  handleUpload([file]);
                }}
                error={
                  touched.invoiceFiles?.[index] &&
                  !!errors.invoiceFiles?.[index]
                }
              />
            ))}
          </div>
          {touched.invoiceFiles &&
            errors.invoiceFiles &&
            typeof errors.invoiceFiles === "string" && (
              <div className="cx-text-text-error cx-text-xs ">
                {errors.invoiceFiles}
              </div>
            )}
        </div>

        <div className="cx-text-red-500 cx-text-sm cx-p-2 cx-mb-2">{error}</div>
        {isBulkAmountOverAvailableLimit && (
          <div className="cx-text-text-error cx-text-sm cx-px-2 cx-mb-2">
            {`Total requested amount (${summaryCurrency} ${bulkParseState.totalAmount?.toLocaleString()}) exceeds your approved credit line available limit (${summaryCurrency} ${availableLimit.toLocaleString()}).`}
          </div>
        )}

        <div className="cx-border-t cx-border-[#D9D9D9] cx-mb-8 cx-pt-8 cx-flex cx-justify-end cx-gap-4 cx-flex-wrap">
          <Button
            label="Cancel"
            outlined
            onClick={previousStep}
            disabled={uploading || loading}
            slim
            className="cx-px-16 cx-w-44"
          />
          <Button
            label="Submit"
            disabled={
              uploading ||
              loading ||
              (invoiceEntryMode === InvoiceEntryMode.MANUAL
                ? !values.invoices.length ||
                  !values.invoiceFiles.length ||
                  !isValid ||
                  !dirty
                : bulkParseState.status !== BulkParseStatus.SUCCESS ||
                  isBulkAmountOverAvailableLimit ||
                  !values.invoiceFiles.length ||
                  !isValid)
            }
            loader={loading ? "right" : undefined}
            type="submit"
            slim
            className="cx-px-16 cx-w-44"
          />
        </div>
      </div>
      <Modal
        isOpen={bulkTemplateModalOpen}
        onClose={() => {
          setBulkTemplateModalOpen(false);
          setBulkTemplateDraftFile(null);
          setBulkTemplateError("");
        }}
        title="Upload drawdown template"
        variant="default"
      >
        <div className="cx-flex cx-flex-col cx-gap-4">
          <Dropzone
            variant="invoice"
            extensions={["Supports up to 1 .xlsx file (max 5 MB)"]}
            showExtensions={false}
            options={{
              accept: {
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [".xlsx"],
              },
              maxFiles: 1,
              maxSize: 5 * 1024 * 1024,
              onDropRejected: (rejections) => {
                if (!rejections?.length) return;
                const messages = new Set<string>();
                rejections.forEach((rej) => {
                  rej.errors.forEach((err) => {
                    switch (err.code) {
                      case "file-too-large":
                        messages.add("Each file must be 5 MB or less.");
                        break;
                      case "too-many-files":
                        messages.add("You can upload only one template file.");
                        break;
                      case "file-invalid-type":
                        messages.add(
                          "Please upload a valid .xlsx template file."
                        );
                        break;
                      default:
                        if (err.message) messages.add(err.message);
                    }
                  });
                });
                setBulkTemplateError(Array.from(messages).join(" "));
              },
            }}
            onDrop={(files) => {
              if (!files?.length) return;
              setBulkTemplateError("");
              setBulkTemplateDraftFile(files[0]);
            }}
            error={bulkTemplateError}
          />

          {bulkTemplateDraftFile && (
            <CardList
              variant={CardVariantType.UPLOADED}
              title={bulkTemplateDraftFile.name}
              description={formatBytes(bulkTemplateDraftFile.size, 2)}
              icon={getFileIcon(bulkTemplateDraftFile)}
              displayVariant={DisplayVariant.NEW}
              handleFileClose={() => {
                setBulkTemplateDraftFile(null);
                setBulkTemplateError("");
              }}
            />
          )}

          <div className="cx-w-full cx-flex cx-flex-col cx-gap-3">
            <Button
              label="Submit"
              fullWidth
              disabled={!bulkTemplateDraftFile || bulkParsing}
              onClick={async () => {
                if (!bulkTemplateDraftFile) return;
                setBulkTemplateFile(bulkTemplateDraftFile);
                setBulkTemplateModalOpen(false);
                await onBulkParseTemplate(bulkTemplateDraftFile);
                setBulkTemplateDraftFile(null);
              }}
              loader={bulkParsing ? "right" : undefined}
              slim={false}
            />
            <Button
              label="Cancel"
              fullWidth
              secondary
              onClick={() => {
                setBulkTemplateModalOpen(false);
                setBulkTemplateDraftFile(null);
                setBulkTemplateError("");
              }}
            />
          </div>
        </div>
      </Modal>
    </form>
  );
};

export default DrawdownForm;
